import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import {
  UploadComponent,
  FileInfo,
  FileRestrictions,
  SelectEvent,
  UploadProgressEvent,
} from '@progress/kendo-angular-upload';
import { IGallery } from 'bi-interfaces/lib/interfaces/IGallery';

@Component({
  selector: 'BI-gallery',
  templateUrl: './bi-gallery.component.html',
  styleUrls: ['./bi-gallery.component.scss'],
})
export class BIGalleryComponent implements IGallery, OnChanges {
  @Input() mode: 'single' | 'multiple' = 'single';
  @Input() data: any[] = [];
  @Input() uploadingProgress: any;
  @Input() DomID: any='';
  @Input() keyId: string = '';
  @Input() saveURL: string = '';
  @Input() viewMode: boolean = false;
  @Input() src = `data:image/png;base64,`;
  @Input() uploadRestrictions: FileRestrictions = {
    allowedExtensions: ['.jpg', 'png', '.jpeg', 'gif'],
    maxFileSize: 2097152,
  };
  @Input() CustomUploadRestrictions: any = {
    Width: null,
    Height:null,
  };
  @Input() selectedImage: any;
  @Input() isBanner:boolean=false;
  @Output() onDeleteImage = new EventEmitter();
  @Output() AfterAddImage = new EventEmitter();
  @Output() OnSelect = new EventEmitter();

  public selectedIndex: number = 0;
  public errorMessages: string[]=[];
  public selectedImageData: any;
  public previewSrc: string = '';

  constructor(
    public sanitizer: DomSanitizer,
    private translate: TranslateService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['selectedImage']?.currentValue)
    {
        this.selectedImage=changes?.['selectedImage']?.currentValue + `?${new Date()}`
    }
    if (changes?.['data']?.currentValue) {
      if (this.data.length > 0) {
        this.selectedImageData = this.data[0];
        this.selectedImage = this.sanitizer.bypassSecurityTrustResourceUrl(
          this.src + this.data[0][this.keyId]
        );
      } else {
        this.selectedImage = null;
      }
    }
  }

  public deleteImage(imageData: any) {
    this.selectedIndex=0;
    this.onDeleteImage.emit({
      data: imageData ?? this.selectedImageData,
    });
  }

  public selectImage(image: any, index: number): void {
    this.selectedImage = this.sanitizer.bypassSecurityTrustResourceUrl(
      this.src + image[this.keyId]
    );
    this.selectedIndex = index;
    this.selectedImageData = image;
    this.OnSelect.emit(image);
  }

  public prev() {
    this.selectedIndex =
      (this.selectedIndex - 1 + this.data.length) % this.data.length;
    this.selectedImage = this.sanitizer.bypassSecurityTrustResourceUrl(
      this.src + this.data[this.selectedIndex][this.keyId]
    );
    this.selectedImageData = this.data[this.selectedIndex];
  }

  public next() {
    this.selectedIndex = (this.selectedIndex + 1) % this.data.length;
    this.selectedImage = this.sanitizer.bypassSecurityTrustResourceUrl(
      this.src + this.data[this.selectedIndex][this.keyId]
    );
    this.selectedImageData = this.data[this.selectedIndex];
  }

  public onSelectImg(ev: SelectEvent, uploader: UploadComponent): void {
    if (uploader.fileList?.files[0]?.length > 0){
      uploader.clearFiles();
    }
    this.SetErrorMessages(ev);
    if (!ev.files[0].validationErrors) {
      let file: FileInfo = ev.files[0];
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.previewSrc = e.target.result;
        const image = new Image();
        image.src = e.target.result,
        image.title = file.name;
        image.onload = () => {
          if(this.HasCustomRestrictions()){
            this.CheckCustomRestrictions(ev,image);
          }
          if(!ev.files[0].validationErrors || ev.files[0].validationErrors.length==0){
           uploader.uploadFiles();
          }else{
           ev.preventDefault();
          }
        }
      };
      if (file.rawFile !== undefined) {
        reader.readAsDataURL(file.rawFile);
      } else {
        console.error("rawFile is undefined.");
      }
    }
  }

  public SetErrorMessages(ev:SelectEvent){
    let errorMessagesSet = new Set<string>();

    // Iterate over each file and add validation errors to the Set
    ev.files.forEach((file) => {
      const errors = file.validationErrors ?? []; // Use an empty array if validationErrors is undefined
      errors.forEach((error) => {
        errorMessagesSet.add(error);
      });
    });

    // Convert the Set to an array and translate each error message
    this.errorMessages = Array.from(errorMessagesSet).map((error) => this.translate.instant(error));
  }

  HasCustomRestrictions():boolean{
    if((this.CustomUploadRestrictions.Width!=null && this.CustomUploadRestrictions.Width!=undefined)
    || (this.CustomUploadRestrictions.Height!=null && this.CustomUploadRestrictions.Height!=undefined)){
      return true;
    }
    return false;
  }
  CheckCustomRestrictions(ev: SelectEvent,image:any){
    let errorMessagesSet = new Set<string>();
    ev.files[0].validationErrors=[];
    if(this.CustomUploadRestrictions.Width!=null && this.CustomUploadRestrictions.Width!=undefined){
      if(image.naturalWidth!=this.CustomUploadRestrictions.Width)
      {
        let errorMsg=this.translate.instant('ImageWidthShouldBe')+this.CustomUploadRestrictions.Width+"px";
        ev.files[0].validationErrors.push(errorMsg);
        errorMessagesSet.add(errorMsg);
      }
    }
    if(this.CustomUploadRestrictions.Height!=null && this.CustomUploadRestrictions.Height!=undefined){
      if(image.naturalHeight!=this.CustomUploadRestrictions.Height)
      {
        let errorMsg=this.translate.instant('ImageHeightShouldBe')+this.CustomUploadRestrictions.Height+"px";
        ev.files[0].validationErrors.push(errorMsg);
        errorMessagesSet.add(errorMsg);
      }
    }
    this.errorMessages = Array.from(errorMessagesSet).map((error) => error );
  }

  public onclick() {
    let btn = document.querySelector(
      '#id_upload_area'+this.DomID+' button'
    ) as HTMLButtonElement;
    btn.click();
  }

  public imageAddedSuccessfully(event: any) {
    this.selectedIndex=0;
    this.AfterAddImage.emit({ event: 'Image Added' });
  }

  public uploadProgressEventHandler(e: UploadProgressEvent) {
    // console.log(e.files[0].name + ' is ' + e.percentComplete + ' uploaded');
  }
}
